<script >
import { computed, ref } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  metrics: {
    type: Array,
    default: () => []
  },
  availableMetrics: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  isMutating: {
    type: Boolean,
    default: false
  },
  error: {
    type: [Object, String],
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const metricPopover = ref(null);
  const isInitialLoading = computed(() => props.isLoading && !props.metrics.length);
  const errorMessage = computed(() => typeof props.error === 'string' ? props.error : props.error?.message || '');
  const hasRestrictedMetrics = computed(() => props.availableMetrics.some(metric => metric.restricted));
  const toggleMetricPopover = event => {
    metricPopover.value?.toggle(event);
  };
  return {
    props,
    emit,
    metricPopover,
    isInitialLoading,
    errorMessage,
    hasRestrictedMetrics,
    toggleMetricPopover
  };
};
export default __sfc_main;
</script>

<template>
    <div class="charting-b-metric-strip" aria-label="Métricas del valor">
        <div v-if="isInitialLoading" class="charting-b-metric-strip-loading">
            <ProgressSpinner style="width: 20px; height: 20px" stroke-width="6" />
        </div>

        <template v-else>
            <template v-for="metric in props.metrics">
                <button
                    v-if="metric.removable"
                    :key="`removable-${metric.field}`"
                    type="button"
                    class="charting-b-metric-chip is-removable"
                    :class="{ 'is-empty': metric.isEmpty }"
                    :disabled="props.isMutating"
                    :aria-label="`Quitar ${metric.label}`"
                    @click="emit('remove-metric', metric.field)"
                >
                    <span class="charting-b-metric-label">{{ metric.label }}</span>
                    <strong class="charting-b-metric-value">{{ metric.value }}</strong>
                    <span class="charting-b-metric-remove-overlay" aria-hidden="true">Quitar</span>
                </button>

                <div v-else :key="`fixed-${metric.field}`" class="charting-b-metric-chip is-fixed" :class="{ 'is-empty': metric.isEmpty }">
                    <span class="charting-b-metric-label">{{ metric.label }}</span>
                    <strong class="charting-b-metric-value">{{ metric.value }}</strong>
                </div>
            </template>

            <Button icon="pi pi-plus" size="small" severity="secondary" outlined rounded class="charting-b-metric-add" :disabled="props.isMutating || !props.availableMetrics.length" aria-label="Añadir métrica" @click="toggleMetricPopover" />
        </template>

        <span v-if="errorMessage" class="charting-b-metric-error">{{ errorMessage }}</span>

        <Popover ref="metricPopover">
            <div class="charting-b-metric-popover">
                <div class="charting-b-metric-popover-title">Métricas</div>
                <div v-if="props.availableMetrics.length" class="charting-b-metric-popover-grid">
                    <Button
                        v-for="metric in props.availableMetrics"
                        :key="metric.field"
                        :label="metric.label"
                        :icon="metric.restricted ? 'pi pi-lock' : ''"
                        size="small"
                        severity="secondary"
                        outlined
                        :disabled="props.isMutating || metric.restricted"
                        :title="metric.restrictionReason || `Añadir ${metric.label}`"
                        :aria-label="metric.restrictionReason ? `${metric.label}. ${metric.restrictionReason}` : `Añadir ${metric.label}`"
                        @click="emit('add-metric', metric.field)"
                    />
                </div>
                <div v-else class="charting-b-metric-empty">No hay más métricas disponibles.</div>
                <div v-if="hasRestrictedMetrics" class="charting-b-metric-restriction-help"><i class="pi pi-lock" aria-hidden="true"></i> Las opciones con candado dependen del plan contratado.</div>
            </div>
        </Popover>
    </div>
</template>

<style scoped>
.charting-b-metric-strip {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
}

.charting-b-metric-strip-loading {
    display: flex;
    min-height: 2.1rem;
    align-items: center;
}

.charting-b-metric-chip {
    position: relative;
    display: inline-grid;
    min-width: 5.4rem;
    max-width: 8.8rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
        'label'
        'value';
    align-items: center;
    overflow: hidden;
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.2));
    border-radius: 0.65rem;
    background: color-mix(in srgb, var(--surface-card) 92%, transparent);
    padding: 0.34rem 0.55rem;
    color: inherit;
    text-align: center;
}

button.charting-b-metric-chip {
    appearance: none;
    font: inherit;
}

.charting-b-metric-chip.is-removable {
    cursor: pointer;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.charting-b-metric-chip.is-fixed {
    border-color: color-mix(in srgb, var(--brand-primary), transparent 66%);
}

.charting-b-metric-chip.is-removable:disabled {
    cursor: wait;
    opacity: 0.68;
}

.charting-b-metric-chip.is-empty::before {
    content: '';
    position: absolute;
    inset: 0;
    background: color-mix(in srgb, var(--p-surface-400, #94a3b8) 14%, transparent);
    pointer-events: none;
}

.charting-b-metric-label {
    grid-area: label;
    position: relative;
    z-index: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.1;
    color: var(--text-color-secondary);
}

.charting-b-metric-value {
    grid-area: value;
    position: relative;
    z-index: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.82rem;
    font-weight: 800;
    line-height: 1.15;
    color: var(--text-color);
}

.charting-b-metric-remove-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, var(--p-surface-500, #64748b) 22%, var(--surface-card));
    color: var(--p-text-color);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.16s ease;
    z-index: 2;
}

.charting-b-metric-chip.is-removable:not(:disabled):hover,
.charting-b-metric-chip.is-removable:not(:disabled):focus-visible {
    border-color: color-mix(in srgb, var(--p-surface-600, #475569) 42%, transparent);
    box-shadow: 0 8px 18px color-mix(in srgb, #0f172a 8%, transparent);
}

.charting-b-metric-chip.is-removable:not(:disabled):hover .charting-b-metric-remove-overlay,
.charting-b-metric-chip.is-removable:not(:disabled):focus-visible .charting-b-metric-remove-overlay {
    opacity: 0.96;
}

::v-deep .charting-b-metric-add.p-button {
    width: 2.05rem;
    min-width: 2.05rem;
    height: 2.05rem;
}

.charting-b-metric-error {
    max-width: 18rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.74rem;
    color: var(--p-red-500);
}

.charting-b-metric-popover {
    display: flex;
    width: min(36rem, calc(100vw - 3rem));
    max-width: 100%;
    flex-direction: column;
    gap: 0.65rem;
}

.charting-b-metric-popover-title {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.charting-b-metric-popover-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.charting-b-metric-empty {
    color: var(--text-color-secondary);
    font-size: 0.82rem;
}

.charting-b-metric-restriction-help {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--p-text-muted-color);
    font-size: 0.75rem;
}

@media (max-width: 1199px) {
    .charting-b-metric-strip {
        width: 100%;
    }
}
</style>
